/*                                     */
/*                                     */
import type { Class } from "type-fest";
import { onLoad } from "@otto-ec/global-resources/dom";
import { addLabels, isActive, sendCustomError } from "@otto-ec/global-resources/apm";
import { focusDeepWithin } from "@otto-ec/otto-components-utils/utils/focus/focusDeepWithin";
import { componentsScope } from "./utils/logger";
import { completeElementInit, registerElementInit } from "./performance";

/**
 *
 *
 *
 *
 */
const PROPR_DEF_KEY = "$$p_d";
const log = componentsScope.scope("extendCustomElement");

declare global {
  interface Window {
    /**
 *
 *
 */
    __components: {
      /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
      extend: typeof extendCustomElement;
    };
  }
}

/**
 *
 *
 */
export type CustomElement = Class<
  HTMLElement & {
    /**
 *
 */
    connectedCallback(): void;
    /**
 *
 */
    disconnectedCallback(): void;
    /**
 *
 */
    adoptedCallback(): void;
    /**
 *
 *
 *
 *
 */
    attributeChangedCallback(name: string, oldValue: string, newValue: string): void;
  }
> & {
  /**
 *
 */
  readonly observedAttributes: string[];
};

/*                            */
const LabelPrefix = "components-count_";

const instanceMap = new Map<string, null[]>();

let instances = 0;

/*                                                        */
export function addInstance(tag: string): void {
  if (!instanceMap.has(tag)) instanceMap.set(tag, []);
  const list = instanceMap.get(tag)!;
  list.push(null);
  instances += 1;
}

/*                                                                    */
if (import.meta.env.PROD && !import.meta.env.STORYBOOK) {
  /*                    */
  onLoad.sub(async () => {
    /*                                              */
    if (!(await isActive())) return;

    /*                                    */
    addLabels(
      /*                                          */
      Object.fromEntries([
        /*                           */
        [`${LabelPrefix}total`, instances],
        /*                          */
        ...[...instanceMap.entries()].map(([tag, list]) => [
          `${LabelPrefix}${tag.toLowerCase()}`,
          list.length,
        ]),
      ]),
    );

    instanceMap.clear();
  });
}

/**
 *
 *
 *
 *
 *
 *
 */
export function cleanUpFalsyBooleanAttributes(host: HTMLElement): void {
  /*                                                      */

  const propDefs = (host as never)[PROPR_DEF_KEY] as
    | Record<string, { type: string; attribute?: string }>
    | undefined;

  /*                                                          */
  if (!propDefs || typeof propDefs !== "object") {
    log.error("Could not find property definitions on", host, "by", PROPR_DEF_KEY);
    sendCustomError(
      "oc-prop-definitions-not-found",
      `Could not find property definitions on element: ${host.tagName.toLowerCase()}`,
      {
        component: host.tagName.toLowerCase(),
        propDefKey: PROPR_DEF_KEY,
      },
    );
    return;
  }

  Object.entries(propDefs)
    /*                                          */
    .filter(([, def]) => !!def && typeof def === "object" && def.type === "Boolean")
    .forEach(([propName, def]) => {
      const attrName = def.attribute ?? propName;
      if (host.getAttribute(attrName) === "false") {
        host.removeAttribute(attrName);
      }
    });
}

/**
 *
 *
 *
 *
 */
function extendCustomElement({
  formAssociated,
  inputBehavior,
}: {
  /**
 *
 *
 */
  formAssociated?: true;
  /**
 *
 *
 */
  inputBehavior?: true;
} = {}) {
  return (customElement: Class<HTMLElement>): CustomElement => {
    let result = class extends (customElement as CustomElement) {
      constructor(...args: unknown[]) {
        super(...args);
        registerElementInit(this);
      }

      connectedCallback(): void {
        /*                               */
        addInstance(this.tagName);

        /*                                                   */
        cleanUpFalsyBooleanAttributes(this);

        const res = super.connectedCallback();
        completeElementInit(this);

        /*                                                                         */
        return res;
      }

      /*                                      */
      focus(): void {
        /*                            */
        /*                                                                    */
        /*                                                            */
        /*                                                                                     */
        /*                                                                                                                                                      */
        /*                                                                                                    */
        /*                                                                                                                */
        focusDeepWithin(this);
      }
    };

    if (formAssociated) {
      result = class extends result {
        static formAssociated = true;

        internals: ElementInternals;

        constructor() {
          super();
          this.internals = this.attachInternals() as unknown as ElementInternals;
        }

        get form(): HTMLFormElement | null {
          return this.internals.form;
        }

        set form(value: unknown) {
          if (typeof value === "string") {
            this.setAttribute("form", value);
          }
        }

        formResetCallback(): void {
          /*                                                         */
          /*         */
          this.resetForm?.();
        }
      };
    }

    if (inputBehavior) {
      result = class extends result {
        get defaultValue(): string {
          return this.getAttribute("value") ?? "";
        }

        set defaultValue(value: string) {
          this.setAttribute("value", value);
        }

        get defaultChecked(): boolean {
          return this.hasAttribute("checked");
        }

        set defaultChecked(value: boolean) {
          if (value) {
            this.setAttribute("checked", "");
          } else {
            this.removeAttribute("checked");
          }
        }
      };
    }

    return result;
  };
}

window.__components ??= {} as never;
window.__components.extend = extendCustomElement;
